/**
 * PrairieBiome - The fourth biome (1500-2000 miles)
 *
 * Prairie characteristics:
 * - Endless golden grasslands under big open skies
 * - Rolling hills with tall wheat and grass
 * - Long straight roads stretching to the horizon
 * - Warm golden and amber color palette
 */

import { BiomeRenderer } from '../BiomeRenderer.js';
import { createBiomeConfig, validateBiomeConfig } from './BiomeConfig.js';

// Prairie-specific color palettes for each time of day
// Index: 0=Day, 1=Sunset, 2=Night, 3=Dawn
export const PRAIRIE_COLORS = [
    // ═══════════════════════════════════════════════════════════════════════════
    // DAY - Golden wheat and warm earth
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Golden wheat
            0x9a8a50, 0xaa9a60, 0x8a7a40,
            // Dry grass
            0x8a9a60, 0x7a8a50, 0x9aaa70,
            // Earth tones
            0x7a6a40, 0x8a7a50, 0x6a5a30
        ],
        rockColors: [
            0x8a8070,   // Prairie stone
            0x9a9080,   // Light rock
            0x7a7060,   // Warm gray
            0xaaa090    // Pale boulder
        ],
        shrubColors: [
            0x6a7a40,   // Tall grass
            0x7a8a50,   // Golden green
            0x8a9a60,   // Wheat
            0x5a6a30    // Shadow grass
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SUNSET - Amber waves of grain
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Amber gold
            0xba8a40, 0xca9a50, 0xaa7a30,
            // Warm orange highlights
            0xaa7a50, 0xba8a60, 0x9a6a40,
            // Deep shadows
            0x7a5a30, 0x8a6a40, 0x6a4a20
        ],
        rockColors: [
            0x9a7050,   // Warm stone
            0xaa8060,   // Sunset rock
            0x8a6040,   // Amber rock
            0xba9070    // Golden boulder
        ],
        shrubColors: [
            0x8a7a30,   // Golden grass
            0x9a8a40,   // Amber wheat
            0xaa9a50,   // Bright gold
            0x6a5a20    // Shadow
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // NIGHT - Silver moonlit prairie
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Moonlit silver-blue
            0x4a5a60, 0x5a6a70, 0x3a4a50,
            // Dark grass
            0x3a4a40, 0x4a5a50, 0x2a3a30,
            // Shadow earth
            0x2a3a38, 0x3a4a48, 0x1a2a28
        ],
        rockColors: [
            0x4a4a50,   // Night stone
            0x5a5a60,   // Moonlit rock
            0x3a3a40,   // Dark rock
            0x6a6a70    // Pale boulder
        ],
        shrubColors: [
            0x2a3a30,   // Night grass
            0x3a4a40,   // Dark wheat
            0x1a2a20,   // Deep shadow
            0x2a3528    // Moonlit grass
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // DAWN - Soft pink and gold morning
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Soft morning gold
            0x9a9a70, 0xaaaa80, 0x8a8a60,
            // Pink-tinged grass
            0x9a8a78, 0xaa9a88, 0x8a7a68,
            // Cool shadows
            0x7a7a60, 0x8a8a70, 0x6a6a50
        ],
        rockColors: [
            0x8a8a80,   // Morning stone
            0x9a9a90,   // Pale rock
            0x7a7a70,   // Cool gray
            0xaaaaa0    // Light boulder
        ],
        shrubColors: [
            0x6a7a50,   // Morning grass
            0x7a8a60,   // Soft gold
            0x5a6a40,   // Cool green
            0x6a7a48    // Dewy grass
        ]
    }
];

export class PrairieBiome extends BiomeRenderer {
    constructor(scene, camera, worldTranslator, config = {}) {
        // Prairie-specific configuration
        const prairieConfig = createBiomeConfig({
            // ─────────────────────────────────────────────────────────────
            // ROAD - Long straight roads with gentle curves
            // ─────────────────────────────────────────────────────────────
            roadWidth: 11,                       // Wide prairie highway
            chunkLength: 50,
            pathSegmentLength: 200,              // Long straight segments
            pathLookAhead: 120,
            roadMaxLateralOffset: 120,           // Gentle curves
            roadMaxVerticalOffset: 15,           // Very gentle hills
            roadSplineTension: 0.0,

            // ─────────────────────────────────────────────────────────────
            // TERRAIN - Flat rolling grasslands
            // ─────────────────────────────────────────────────────────────
            terrainColor: 0x928a20,              // Golden grass
            terrainAmplitudeLarge: 80,           // Gentle rolling
            terrainAmplitudeMedium: 35,          // Subtle waves
            terrainAmplitudeSmall: 12,           // Minor bumps
            terrainAmplitudeFine: 4,             // Smooth surface
            terrainHeightMultiplier: 0.5,        // Low terrain
            waterLevel: -40,                     // No visible water

            // ─────────────────────────────────────────────────────────────
            // TERRAIN DIMENSIONS
            // ─────────────────────────────────────────────────────────────
            terrainWidth: 1500,                  // Wide open vistas
            terrainSegmentsOut: 16,
            terrainSegmentsAlong: 1,

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE STRIP
            // ─────────────────────────────────────────────────────────────
            roadsideStripWidth: 30,
            roadsideStripSegments: 3,
            roadsideStripOverlap: 0.03,
            mountainOverlapIntoStrip: 5,

            // ─────────────────────────────────────────────────────────────
            // TERRAIN FEATURES - Gentle rolling hills
            // ─────────────────────────────────────────────────────────────
            terrainFeaturesEnabled: true,
            terrainFeatureScale: 0.00002,        // Large-scale gentle features
            terrainFeatureWeights: {
                dunes: 0.8,       // Rolling waves of grain
                peaks: 0.05,      // Very rare
                plateaus: 0.4,    // Flat areas
                spires: 0.0,      // None
                cliffs: 0.05,     // Rare ravines
                valleys: 0.3      // Gentle dips
            },
            terrainFeatureBlend: 0.7,

            // ─────────────────────────────────────────────────────────────
            // TERRAIN HEIGHT LAYERING - Very gradual
            // ─────────────────────────────────────────────────────────────
            terrainLayerHeights: [0.2, 0.35, 0.5, 0.7],
            terrainLayerDistances: [0.2, 0.5, 0.8, 1.0],

            // ─────────────────────────────────────────────────────────────
            // FLAT ZONE - Wide open areas near road
            // ─────────────────────────────────────────────────────────────
            terrainFlatZoneMin: 0.02,
            terrainFlatZoneMax: 0.25,            // Wide flat areas
            terrainBlendRange: 0.2,

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE OBJECTS - Sparse grass and occasional rocks
            // ─────────────────────────────────────────────────────────────
            enableRoadsideObjects: true,
            roadsideObjectSpawnDistance: 800,
            roadsideObjectDensity: 0.25,         // Sparse vegetation
            roadsideObjectMinSafeZone: 12,
            roadsideObjectDistributionWidth: 200,
            rockProbability: 0.15,               // Few rocks
            rockMinSize: 1,
            rockMaxSize: 3,
            shrubMinSize: 2,                     // Short grass clumps
            shrubMaxSize: 8,
            shrubSpriteCount: 3,
            shrubScaleVariation: 0.4,
            shrubStretchVariation: 0.5,          // Stretched grass
            roadsideObjectFadeInDuration: 0.4,

            // Prairie sprites (using shrubs as placeholder)
            shrubSpritePaths: [
                'assets/sprites/shrub1.png',
                'assets/sprites/shrub2.png',
                'assets/sprites/shrub3.png'
            ],

            // Merge with any user-provided config
            ...config
        });

        // Validate configuration in development
        validateBiomeConfig(prairieConfig, 'PrairieBiome');

        super(scene, camera, worldTranslator, prairieConfig);

        // Biome identity
        this.biomeName = 'Prairie';
        this.minMiles = 1500;
        this.maxMiles = 2000;

        // Store biome-specific color palettes
        this.biomeColors = PRAIRIE_COLORS;
        this.palette.setBiomeColors(PRAIRIE_COLORS);
    }

    getTerrainColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.terrainColors : [];
    }

    getRockColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.rockColors : [];
    }

    getShrubColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.shrubColors : [];
    }
}

export const PRAIRIE_BIOME_ENTRY = {
    minMiles: 1500,
    maxMiles: 2000,
    BiomeClass: PrairieBiome,
    name: 'Prairie'
};
